import { useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { log } from "@/lib/logger";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function makeMessage(role, text) {
  return { id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`, role, text };
}

/** Parse a raw SSE frame chunk and yield JSON payloads (excluding [DONE]). */
function* parseSSE(buffer) {
  const events = buffer.split("\n\n");
  const rest = events.pop() || "";
  for (const ev of events) {
    const line = ev.trim();
    if (!line.startsWith("data:")) continue;
    const payload = line.slice(5).trim();
    if (payload === "[DONE]") continue;
    try {
      yield JSON.parse(payload);
    } catch (parseErr) {
      log.warn("Ask AI stream parse error:", parseErr, payload);
    }
  }
  return rest;
}

/** Streaming AI chat scoped to a single candidate. */
export function useCandidateChat(candidateId) {
  const [messages, setMessages] = useState([]);
  const [streaming, setStreaming] = useState(false);
  const streamingRef = useRef(false);

  const appendDelta = useCallback((delta) => {
    setMessages((prev) => {
      const last = prev[prev.length - 1];
      if (!last || last.role !== "ai") return prev;
      return [...prev.slice(0, -1), { ...last, text: last.text + delta }];
    });
  }, []);

  const ask = useCallback(async (question) => {
    const trimmed = question.trim();
    if (!trimmed || streamingRef.current) return;
    streamingRef.current = true;
    setStreaming(true);
    setMessages((prev) => [...prev, makeMessage("user", trimmed), makeMessage("ai", "")]);
    try {
      const res = await fetch(`${BACKEND}/api/ai/ask`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ candidate_id: candidateId, question: trimmed }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(formatApiError(err.detail) || `Request failed (${res.status})`);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        // Split buffer into events; keep the last (potentially incomplete) chunk.
        const events = buf.split("\n\n");
        buf = events.pop() || "";
        for (const raw of events) {
          const parsedIter = parseSSE(raw + "\n\n");
          for (const parsed of parsedIter) {
            if (parsed?.delta) appendDelta(parsed.delta);
          }
        }
      }
    } catch (e) {
      toast.error(e.message || "Ask AI failed");
      setMessages((prev) => prev.slice(0, -1));
    } finally {
      streamingRef.current = false;
      setStreaming(false);
    }
  }, [candidateId, appendDelta]);

  return { messages, streaming, ask };
}
